import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Auth actions context — the `logout`, `refreshSession`, and `setUser`
 * methods from the frontend `AuthProvider`, injected into SDK hooks so
 * `useAuth()` can expose the complete `AuthContextValue` shape without
 * depending on frontend-specific providers.
 *
 * Platform-neutral: pure React context. No web/RN globals, no vendor
 * imports beyond React. Safe in `src/auth/` core per
 * `.claude/rules/platform-agnostic.md`.
 *
 * Wiring: the frontend `AuthProvider` mounts an `<AuthActionsProvider>`
 * directly wrapping its children:
 *
 * ```tsx
 * <AuthActionsProvider value={{ logout, refreshSession, setUser }}>
 *   {children}
 * </AuthActionsProvider>
 * ```
 *
 * When no provider is mounted (e.g. unit tests, Storybook), the hooks
 * return no-op stubs so they don't throw.
 */
import { createContext, useContext } from 'react';
// ── Stubs ────────────────────────────────────────────────────────────────────
const NOOP_LOGOUT = () => Promise.resolve();
const NOOP_REFRESH = () => Promise.resolve();
const NOOP_SET_USER = _user => {};
const DEFAULT_AUTH_ACTIONS = Object.freeze({
  logout: NOOP_LOGOUT,
  refreshSession: NOOP_REFRESH,
  setUser: NOOP_SET_USER
});
// ── Context ──────────────────────────────────────────────────────────────────
const AuthActionsContext = createContext(DEFAULT_AUTH_ACTIONS);
/** Returns the auth action methods provided by the nearest `<AuthActionsProvider>`. */
export function useAuthActions() {
  return useContext(AuthActionsContext);
}
/**
 * Provides the imperative auth methods (`logout`, `refreshSession`, `setUser`)
 * to any `useAuth()` consumer below in the tree.
 *
 * The frontend `AuthProvider` mounts this once, passing its own
 * `logout`, `refreshSession`, and `setUser` implementations.
 */
export function AuthActionsProvider({
  value,
  children
}) {
  return _jsx(AuthActionsContext.Provider, {
    value: value,
    children: children
  });
}